import React from 'react';
import Badge from './Badge';
import Certificate from './Certificate';
import { resolveSubjectLabel } from '../../utils/achievements';
import styles from './AchievementModal.module.css';

/**
 * Congratulation pop-up (manual close only — no auto-dismiss).
 *
 * achievement shapes:
 *  { type:'chapter', title, meta:{hue,hue2,emoji,label}, subjectKey }
 *  { type:'subject', meta:{...}, badgeCount }
 *  { type:'program', meta:{...} }
 * certificate: optional second view rendered when a subject/program completes
 *   alongside the final chapter badge (shows both in one pop-up).
 */
const AchievementModal = ({ achievement, certificate, studentName, preparingFor = '', onClose }) => {
  if (!achievement) return null;
  const { meta = {}, title } = achievement;
  const isChapter = achievement.type === 'chapter';
  const isSubject = achievement.type === 'subject';
  const isProgram = achievement.type === 'program';

  // JEE/NEET share physics & chemistry — personalise the label by preparingFor
  const subjectLabel = achievement.subjectKey
    ? resolveSubjectLabel(achievement.subjectKey, preparingFor)
    : meta.label;

  const heading = isChapter
    ? 'Chapter Completed!'
    : isSubject
      ? 'Subject Completed!'
      : 'Program Completed!';

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-label="Achievement unlocked">
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">✕</button>

        <div className={styles.confettiBar} style={{ background: `linear-gradient(90deg, ${meta.hue}, ${meta.hue2})` }} />

        <div className={styles.body}>
          <p className={styles.congrats}>🎉 Congratulations{studentName ? `, ${studentName}` : ''}!</p>
          <h3 className={styles.heading}>{heading}</h3>

          {(isChapter || isProgram) && (
            <div className={styles.badgeStage}>
              <Badge
                emoji={meta.emoji}
                hue={meta.hue}
                hue2={meta.hue2}
                title={title}
                subtitle={subjectLabel}
                studentName={studentName}
                size="lg"
                downloadable={false}
              />
            </div>
          )}
          {isChapter && (
            <p className={styles.downloadHint}>💾 Find this badge on your profile to download & share it!</p>
          )}

          {isChapter && (
            <>
              <p className={styles.awardName}>{title}</p>
              <p className={styles.awardSub}>{subjectLabel} — Badge Unlocked</p>
            </>
          )}
          {isProgram && (
            <>
              <p className={styles.awardName}>{meta.label}</p>
              <p className={styles.awardSub}>All subjects completed — Program Trophy earned!</p>
            </>
          )}

          {(certificate || isSubject || isProgram) && (
            <div className={styles.certWrap}>
              <Certificate
                compact
                studentName={studentName}
                title={certificate?.title || subjectLabel}
                subtitle={certificate?.subtitle || (isSubject ? `All ${achievement.badgeCount} chapters completed` : 'All subjects completed')}
                hue={meta.hue}
                hue2={meta.hue2}
                emoji={meta.emoji}
              />
            </div>
          )}

          <button
            type="button"
            className={styles.ctaBtn}
            style={{ background: `linear-gradient(135deg, ${meta.hue}, ${meta.hue2})` }}
            onClick={onClose}
          >
            Keep Going →
          </button>
          <p className={styles.note}>This achievement is saved to your profile.</p>
        </div>
      </div>
    </div>
  );
};

export default AchievementModal;
